// Операционный (бизнес-) день точки — pos.day_start, как «Открытие смены» у Clopos.
//
// При 03:00 чек, пробитый 25.09 в 02:00, относится к 24.09. Отчёты по датам бэк режет
// сам (App\Helper\BusinessDay), а фронту нужно одно: чтобы «Сегодня» в 01:30 было
// вчерашней датой, иначе после полуночи отчёт открывается пустым.
//
// Час берётся из /helper/poses (шапка грузит его при входе) и кладётся в localStorage,
// чтобы страница, открытая раньше ответа шапки, тоже знала сдвиг. 00:00 — календарные
// сутки, прежнее поведение.

const STORAGE_KEY = 'business_day_start'
const ZONE_STORAGE_KEY = 'business_day_zone'
const MIDNIGHT = '00:00'

let dayStart = readStored()
// Пояс точки (IANA). null — не знаем или у точек разный: «сейчас» берётся по часам браузера, как раньше
let dayZone = readStoredZone()

function readStored() {
	try {
		return normalize(localStorage.getItem(STORAGE_KEY))
	} catch (e) {
		return MIDNIGHT
	}
}

function readStoredZone() {
	try {
		return validZone(localStorage.getItem(ZONE_STORAGE_KEY))
	} catch (e) {
		return null
	}
}

// Название пояса, которое умеет Intl; мусор — null
function validZone(value) {
	if (!value) return null
	try {
		new Intl.DateTimeFormat('en-US', { timeZone: value })
		return value
	} catch (e) {
		return null
	}
}

function normalize(value) {
	const match = /^(\d{1,2}):(\d{2})/.exec(String(value ?? ''))
	if (!match) return MIDNIGHT
	const hours = Number(match[1])
	if (hours > 12) return MIDNIGHT
	return `${String(hours).padStart(2, '0')}:${match[2]}`
}

// Выбор часа по списку точек: у текущей точки — её час; «все точки» — общий час,
// если он у всех одинаковый, иначе полночь (бэк всё равно режет каждую точку своим).
// Пояс выбирается так же: у текущей точки свой, у всех — общий, если одинаковый.
export function setBusinessDayStartFromPoses(poses = [], posId = null) {
	let value = MIDNIGHT
	let zone = null
	const current = posId ? poses.find(item => Number(item.id) === Number(posId)) : null
	if (current) {
		value = normalize(current.day_start)
		zone = validZone(current.timezone)
	} else {
		const starts = [...new Set(poses.map(item => normalize(item.day_start)))]
		if (starts.length === 1) value = starts[0]
		const zones = [...new Set(poses.map(item => validZone(item.timezone)))]
		if (zones.length === 1) zone = zones[0]
	}

	dayStart = value
	dayZone = zone
	try {
		localStorage.setItem(STORAGE_KEY, value)
		if (zone) {
			localStorage.setItem(ZONE_STORAGE_KEY, zone)
		} else {
			localStorage.removeItem(ZONE_STORAGE_KEY)
		}
	} catch (e) { }

	return value
}

// Настенное время пояса как локальная дата браузера: поля Date (getHours, getDate…) —
// это час и число в поясе точки, а не на компьютере кассира. Пояса нет — момент как есть.
function wallClockIn(zone, now) {
	if (!zone) return now
	try {
		const parts = {}
		new Intl.DateTimeFormat('en-US', {
			timeZone: zone, hourCycle: 'h23',
			year: 'numeric', month: '2-digit', day: '2-digit',
			hour: '2-digit', minute: '2-digit', second: '2-digit',
		}).formatToParts(now).forEach(part => { parts[part.type] = part.value })
		return new Date(
			Number(parts.year), Number(parts.month) - 1, Number(parts.day),
			Number(parts.hour), Number(parts.minute), Number(parts.second)
		)
	} catch (e) {
		return now
	}
}

export function getBusinessDayStart() {
	return dayStart
}

export function isBusinessDayShifted() {
	return dayStart !== MIDNIGHT
}

// «Сейчас» бизнес-дня: местное время точки, сдвинутое назад на начало дня. От него считаются
// все пресеты — сегодня, вчера, текущая неделя и месяц.
export function businessNow(now = new Date()) {
	const [hours, minutes] = dayStart.split(':').map(Number)
	const wall = wallClockIn(dayZone, now)
	return new Date(wall.getTime() - (hours * 60 + minutes) * 60 * 1000)
}

// «Сегодня» бизнес-дня строкой yyyy-MM-dd — начальное значение фильтров отчётов.
export function businessToday(now = new Date()) {
	const date = businessNow(now)
	const pad = (value) => String(value).padStart(2, '0')
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// Первое число текущего бизнес-месяца строкой yyyy-MM-dd: 1-го в 01:30 при начале дня
// 03:00 ещё идёт последний день прошлого месяца — и фильтр «с начала месяца» тоже прошлый.
export function businessMonthStart(now = new Date()) {
	const date = businessNow(now)
	const pad = (value) => String(value).padStart(2, '0')
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-01`
}

// Начало бизнес-дня даты: Date на date + day_start (для пикеров со временем).
export function businessDayStartOf(date) {
	const [hours, minutes] = dayStart.split(':').map(Number)
	const result = new Date(date)
	result.setHours(hours, minutes, 0, 0)
	return result
}

// Последняя секунда бизнес-дня даты: следующий день day_start − 1 с.
export function businessDayEndOf(date) {
	const result = businessDayStartOf(date)
	result.setDate(result.getDate() + 1)
	result.setSeconds(result.getSeconds() - 1)
	return result
}
